// 파티 창(docs/PARTY-TRADE-PRD.md 3장 흐름, 레인 D UI). 게임·렌더와 분리된 DOM 창이고 상태는 PartyClient(src/game/partyNet.ts)를 읽기만 한다.
//
// ── 게임 레인이 main.ts에서 부르는 방법 ─────────────────────────────────────────
//   import { mountPartyUi } from './ui/partyPanel';
//   const partyUi = mountPartyUi({ client: partyClient, heroName: () => hero.name, inVillage: () => view.flow.scene === Scene.Village });
//   // 끝날 때(선택): partyUi.dispose();
// ── 시그니처 ──────────────────────────────────────────────────────────────────
//   mountPartyUi(host: PartyUiHost): PartyUi
//   PartyUiHost { client: PartyClientView(= PartyClient 그대로 넘기면 된다); heroName?: () => string; inVillage: () => boolean;
//                 parent?: HTMLElement(기본 document.body); now?: () => number(서버 시각 Date.now); openMail?: () => void(기본: 우편함 창 토글 이벤트) }
//   PartyUi { open(); close(); toggle(); isOpen(): boolean; tick(); dispose() }
// 스스로 200ms마다 client.revision·시각을 보고 다시 그린다(게임 루프에서 부를 필요 없음). 「파티」 단추는 menuBar가 PARTY_MENU_EVENT('party')로 알리고 이 창이 토글한다.
// 규칙: 마을(inVillage)에서만 창이 열리고, 판이 시작되면(phase run) 창이 닫히며 화면 왼쪽 가장자리에 작은 띠(웨이브·동료 접속/쓰러짐)만 남는다.
//   창은 오른쪽 가장자리(폰 세로는 위쪽 띠)에 붙어 가운데 플레이 영역을 가리지 않는다(partyStyle.ts).
import { HEROES_90D } from '../shared/content/heroes';
import { PARTY_CODE_ALPHABET, PARTY_CODE_RE, PARTY_COUNTDOWN_MS, PARTY_DAILY_REWARDS, type PartyMemberView, type PartyRoomView, type PartyServerMsg } from '../shared/partyNet';
import { assetUrl } from '../pwa/assetMap';
import { el } from './dom';
import { PARTY_MENU_EVENT } from './menuBar';
import { PARTY_WINDOW_EVENT, ensurePartyStyle } from './partyStyle';

/** PartyClient 중 이 창이 읽고 부르는 부분(QA 페이지는 가짜를 넘긴다) */
export interface PartyClientView {
  readonly status: 'idle' | 'connecting' | 'online' | 'retrying' | 'closed';
  readonly room: PartyRoomView | null;
  readonly you: number;
  readonly error: string;
  readonly lastEnd: Extract<PartyServerMsg, { t: 'end' }> | null;
  readonly revision: number;
  create(): void;
  join(code: string): void;
  leave(): void;
  ready(on: boolean): void;
  setTier(r: number): void;
  start(): void;
  kick(slot: number): void;
}
export interface PartyUiHost {
  client: PartyClientView;
  /** 내 영웅 이름(표시용, 없으면 명단의 내 칸 이름) */
  heroName?: () => string;
  /** 마을인가(창은 마을에서만 열린다) */
  inVillage: () => boolean;
  parent?: HTMLElement;
  /** 서버 시각(ms, 기본 Date.now) — 카운트다운 */
  now?: () => number;
  /** 결과 카드 「우편함에서 받기」(기본: 우편함 창 토글 이벤트) */
  openMail?: () => void;
}
export interface PartyUi { open(): void; close(): void; toggle(): void; isOpen(): boolean; tick(): void; dispose(): void }

// ── 순수 표시 규칙(시험 대상) ───────────────────────────────────────

/** 서버·연결 오류 코드 → 한국어 */
export const PARTY_ERROR_KO: Readonly<Record<string, string>> = {
  full: '파티가 가득 찼어요.',
  'no-party': '없는 파티 코드예요. 코드를 다시 확인해 주세요.',
  'in-run': '이미 출발한 파티예요.',
  'need-two': '두 명 이상 모여야 출발할 수 있어요.',
  'join-failed': '파티에 들어가지 못했어요. 코드를 확인하거나 파티가 가득 찼는지 확인해 주세요.',
  'tier-locked': '파티 단계는 서버에서 앞 단계를 함께 깨야 열려요. 난이도를 낮춰 주세요.',
  'not-ready': '모두 준비해야 출발할 수 있어요.',
  'not-leader': '파티장만 할 수 있어요.',
  kicked: '파티에서 나가게 됐어요.',
  replaced: '다른 창에서 접속해 이 창의 파티 연결이 끊겼어요.',
  version: '새 버전이 나왔어요. 새로고침해 주세요.',
  connect: '연결에 실패했어요. 잠시 뒤 다시 해 주세요.',
  'bad-code': '코드는 6자예요.',
};
export function partyErrorText(code: string): string {
  if (!code) return '';
  return PARTY_ERROR_KO[code] ?? '파티에 문제가 생겼어요. (' + code + ')';
}

/** 결과 이유 → 한국어(실패 사유 / 성공이어도 보상이 없는 사유) */
export const END_REASON_KO: Readonly<Record<string, string>> = {
  wiped: '전멸했어요.',
  timeout: '시간이 다 됐어요.',
  empty: '모두 나가서 끝났어요.',
  'daily-limit': '오늘 보상 횟수를 모두 썼어요.',
  'link-required': 'Google 계정을 연결한 계정만 파티 보상을 받아요(게스트는 연습 판).',
  ledger: '보상 기록이 늦어지고 있어요. 잠시 뒤 우편함을 확인해 주세요.',
};
export function endTitle(end: { ok: boolean }): string { return end.ok ? '토벌 성공!' : '토벌 실패'; }
/** 결과 카드 설명 한 줄(보상이 없는 이유 포함) */
export function endNote(end: { ok: boolean; reason: string; eligible: boolean; reward: { gold: number; items: number } | null }): string {
  const r = END_REASON_KO[end.reason];
  if (end.reward) return '';
  if (r) return r;
  if (!end.ok) return '이번엔 보상이 없어요.';
  if (!end.eligible) return '피해 기여가 적어 이번엔 보상이 없어요.';
  return '';
}

/** 보상이 어디로 가는지(거래소가 켜져 있으면 골드는 거래 금고, 장비는 우편) */
export function rewardWhereText(r: { items: number; vault?: boolean }): string {
  if (!r.vault) return '보상은 우편함으로 가요.';
  return r.items > 0 ? '골드는 거래 금고로, 장비는 우편함으로 가요.' : '골드는 거래 금고로 들어갔어요.';
}

/** 코드 입력 정리: 대문자, 쓰지 않는 글자(I·L·O·0·1) 제거, 6자까지 */
export function sanitizeCode(raw: string): string {
  let out = '';
  for (const c of raw.toUpperCase()) if (PARTY_CODE_ALPHABET.includes(c)) out += c;
  return out.slice(0, 6);
}
export function codeValid(code: string): boolean { return PARTY_CODE_RE.test(code); }

/** 출발 단추 상태: 파티장만 쓰고, 2명 이상 + 파티장 아닌 접속 중 멤버가 모두 준비일 때 켜진다(서버 start 검사와 같다) */
export function startState(room: PartyRoomView, you: number): { enabled: boolean; reason: string } {
  const leader = room.members.some((m) => m.slot === you && m.leader);
  if (!leader) return { enabled: false, reason: '파티장이 출발하면 시작돼요.' };
  const online = room.members.filter((m) => m.online);
  if (online.length < 2) return { enabled: false, reason: '두 명 이상 모여야 출발할 수 있어요.' };
  const wait = online.filter((m) => !m.leader && !m.ready);
  if (wait.length) return { enabled: false, reason: wait.map((m) => m.name).join(', ') + ' 님이 준비하면 출발할 수 있어요.' };
  return { enabled: true, reason: '' };
}

/** 카운트다운 초(3·2·1, 0이면 곧 출발). 서버 시계와 어긋나도 0~3으로 자른다 */
export function countdownSec(startAt: number, now: number): number {
  const s = Math.ceil((startAt - now) / 1000);
  return Math.max(0, Math.min(Math.ceil(PARTY_COUNTDOWN_MS / 1000), s));
}

export interface DmgRow { slot: number; name: string; dmg: number; pct: number; share: number; me: boolean }
/** 피해 기여: dmg[슬롯] → 명단 이름과 합쳐 막대 길이(가장 큰 사람 = 100%)와 전체 대비 비율 */
export function damageRows(dmg: readonly number[], members: readonly PartyMemberView[], you: number): DmgRow[] {
  const total = dmg.reduce((a, b) => a + Math.max(0, b), 0);
  const max = dmg.reduce((a, b) => Math.max(a, b), 0);
  const rows: DmgRow[] = [];
  dmg.forEach((d, slot) => {
    const m = members.find((x) => x.slot === slot);
    if (!m && d <= 0) return;
    rows.push({ slot, name: m?.name ?? '동료', dmg: d, pct: max > 0 ? Math.round((d / max) * 100) : 0, share: total > 0 ? Math.round((d / total) * 100) : 0, me: slot === you });
  });
  return rows.sort((a, b) => b.dmg - a.dmg);
}

export function heroLabel(m: PartyMemberView): string {
  const h = HEROES_90D.find((x) => x.id === m.hero);
  return (h?.name ?? m.hero) + ' Lv.' + m.lv;
}
/** 멤버 상태 칩 */
export function memberChip(m: PartyMemberView): { text: string; tone: 'ok' | 'bad' | 'lead' | '' } {
  if (!m.online) return { text: '접속 끊김', tone: 'bad' };
  if (m.leader) return { text: '파티장', tone: 'lead' };
  return m.ready ? { text: '준비', tone: 'ok' } : { text: '대기', tone: '' };
}
/** 달리는 중 띠 한 칸 */
export function stripMemberState(m: PartyMemberView): 'ok' | 'off' | 'down' { return !m.online ? 'off' : m.down ? 'down' : 'ok'; }

/** 창 모양을 정하는 상태(다시 그릴지 판단하는 열쇠) */
export type PartyView = 'none' | 'lobby' | 'count' | 'run' | 'result';
export function viewOf(room: PartyRoomView | null, end: PartyClientView['lastEnd'], dismissedRun: string, village: boolean): PartyView {
  if (end && end.runId !== dismissedRun && village && (!room || room.phase !== 'run')) return 'result';
  if (!room) return 'none';
  if (room.phase === 'count') return 'count';
  if (room.phase === 'run') return 'run';
  return 'lobby';
}

// ── DOM ────────────────────────────────────────────────────────────

const TIERS = [1, 2, 3, 4] as const;

/** 오늘 남은 보상 문구. 서버가 모를 때(-1 등 음수)는 빈 문자열 — 0/5로 보여 '다 썼다'고 오해하게 하지 않는다(PR #243 Codex P2) */
export function rewardsLeftText(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  return '오늘 남은 보상 ' + Math.min(n, PARTY_DAILY_REWARDS) + '/' + PARTY_DAILY_REWARDS + '판';
}

/** 판 중 나가기 확인 시간: 첫 탭 뒤 이 안에 다시 누르면 나간다 */
export const STRIP_LEAVE_CONFIRM_MS = 3000;
/** 판 중 나가기 탭 한 번 → 나갈지, 다음 확인 시작 시각 */
export function stripLeaveTap(armedAt: number, t: number): { leave: boolean; armedAt: number } {
  if (armedAt > 0 && t - armedAt < STRIP_LEAVE_CONFIRM_MS) return { leave: true, armedAt: 0 };
  return { leave: false, armedAt: t };
}

export function mountPartyUi(host: PartyUiHost): PartyUi {
  ensurePartyStyle();
  const parent = host.parent ?? document.body;
  const now = host.now ?? Date.now;
  const c = host.client;

  const win = el('div', '', parent);
  win.className = 'dh-pw';
  win.dataset.testid = 'party-window';
  win.setAttribute('role', 'dialog');
  win.setAttribute('aria-label', '파티');
  const hd = el('div', '', win); hd.className = 'hd';
  const title = el('h2', '', hd, '파티');
  const xb = el('button', '', hd, '✕'); xb.className = 'x'; xb.type = 'button'; xb.setAttribute('aria-label', '파티 창 닫기'); xb.dataset.testid = 'party-close';
  const bd = el('div', '', win); bd.className = 'bd';
  // 행동 단추(준비·출발·나가기·우편함 받기)는 스크롤 밖 아래 고정 — 좁은 화면에서도 늘 보인다
  const ft = el('div', '', win); ft.className = 'ft';
  const strip = el('div', '', parent); strip.className = 'dh-ps'; strip.dataset.testid = 'party-strip';

  let opened = false;
  let draft = '';
  let copied = 0;
  let dismissedRun = '';
  let lastView: PartyView = 'none';
  let key = '';
  let stripKey = '';
  /** 판 중 나가기 확인(한 번 누르면 3초 동안 '정말 나가기') */
  let leaveArmedAt = 0;
  let seenErr = '';
  let seenResult = '';

  const btn = (parent2: HTMLElement, label: string, fn: () => void, cls = '', testid = ''): HTMLButtonElement => {
    const b = el('button', '', parent2, label);
    b.type = 'button'; b.className = 'btn' + (cls ? ' ' + cls : '');
    if (testid) b.dataset.testid = testid;
    b.addEventListener('click', fn);
    return b;
  };
  const sec = (label: string, foot = false): HTMLDivElement => {
    const s = el('div', '', foot ? ft : bd); s.className = 'sec';
    if (label) el('div', '', s, label).className = 'lb';
    return s;
  };
  const note = (p: HTMLElement, text: string, cls = ''): HTMLElement => { const n = el('div', '', p, text); n.className = 'note' + (cls ? ' ' + cls : ''); n.dataset.testid = 'party-note'; return n; };

  const village = (): boolean => { try { return host.inVillage(); } catch { return false; } };
  const setOpen = (on: boolean): void => {
    if (on && !village()) return;
    if (on === opened) return;
    opened = on;
    if (on) window.dispatchEvent(new CustomEvent<string>(PARTY_WINDOW_EVENT, { detail: 'party' }));
    key = '';
    render();
  };

  function renderNone(): void {
    const connecting = c.status === 'connecting' || c.status === 'retrying';
    const s1 = sec('새 파티');
    note(s1, '친구와 함께 던전을 돌아요. 파티를 만들면 6자 코드가 나와요.');
    const mk = btn(s1, '파티 만들기', () => c.create(), 'pri', 'party-create');
    mk.disabled = connecting;
    const s2 = sec('코드로 참가');
    const input = el('input', '', s2);
    input.className = 'code'; input.type = 'text'; input.inputMode = 'text'; input.maxLength = 12; input.placeholder = '코드 6자';
    input.autocomplete = 'off'; input.autocapitalize = 'characters'; input.spellcheck = false; input.value = draft;
    input.setAttribute('aria-label', '파티 코드 6자'); input.dataset.testid = 'party-code-input';
    const go = btn(s2, '참가', () => { if (codeValid(draft)) c.join(draft); }, 'pri', 'party-join');
    const sync = (): void => { go.disabled = connecting || !codeValid(draft); };
    input.addEventListener('input', () => { draft = sanitizeCode(input.value); if (input.value !== draft) input.value = draft; sync(); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && codeValid(draft) && !connecting) c.join(draft); e.stopPropagation(); });
    input.addEventListener('keyup', (e) => e.stopPropagation());
    sync();
    if (connecting) note(s2, '연결 중이에요…');
    if (c.error) note(bd, partyErrorText(c.error), 'err');
  }

  function renderLobby(room: PartyRoomView): void {
    const me = room.members.find((m) => m.slot === c.you);
    const leader = !!me?.leader;
    const s1 = sec('파티 코드');
    const code = el('div', '', s1, room.code); code.className = 'big'; code.dataset.testid = 'party-code';
    const cp = btn(s1, '코드 복사', () => {
      const done = (ok: boolean): void => { copied = ok ? 1 : -1; key = ''; render(); setTimeout(() => { copied = 0; key = ''; render(); }, 1800); };
      try { void navigator.clipboard.writeText(room.code).then(() => done(true), () => done(false)); } catch { done(false); }
    }, 'sm', 'party-copy');
    if (copied === 1) cp.textContent = '복사됨';
    if (copied === -1) note(s1, '복사가 안 돼요. 코드를 길게 눌러 직접 복사해 주세요.', 'err');
    if (c.status !== 'online') note(s1, '연결이 끊겨 다시 붙는 중이에요…', 'err');

    const s2 = sec('멤버 ' + room.members.length + '/' + room.max);
    for (const m of room.members) {
      const row = el('div', '', s2); row.className = 'mem' + (m.slot === c.you ? ' me' : '') + (m.online ? '' : ' off');
      row.dataset.testid = 'party-member';
      const nm = el('div', '', row); nm.className = 'nm'; nm.textContent = (m.leader ? '👑 ' : '') + m.name;
      el('span', '', row, heroLabel(m)).className = 'sub';
      const ch = memberChip(m);
      el('span', '', row, ch.text).className = 'chip' + (ch.tone ? ' ' + ch.tone : '');
      if (leader && m.slot !== c.you) btn(row, '내보내기', () => c.kick(m.slot), 'sm warn', 'party-kick');
    }
    for (let i = room.members.length; i < room.max; i++) el('div', '', s2, '빈 자리 · 코드를 알려 주세요').className = 'mem empty';

    const s3 = sec('난이도 (지역)');
    const seg = el('div', '', s3); seg.className = 'seg';
    for (const r of TIERS) {
      const b = btn(seg, '지역 ' + r, () => c.setTier(r), room.tier === r ? 'on' : '', 'party-tier-' + r);
      b.disabled = !leader;
      b.setAttribute('aria-pressed', room.tier === r ? 'true' : 'false');
    }
    if (!leader) note(s3, '난이도는 파티장이 정해요.');
    const rl = rewardsLeftText(room.rewardsLeft);
    note(s3, rl || '보상은 하루 ' + PARTY_DAILY_REWARDS + '판까지 받을 수 있어요.');

    const act = sec('', true);
    const st = startState(room, c.you);
    const row = el('div', '', act); row.className = 'row';
    if (!leader && me) btn(row, me.ready ? '준비 취소' : '준비', () => c.ready(!me.ready), (me.ready ? '' : 'pri') + ' grow', 'party-ready');
    if (leader) {
      const sb = btn(row, '출발', () => c.start(), 'pri grow', 'party-start');
      sb.disabled = !st.enabled;
    }
    btn(row, '나가기', () => { c.leave(); }, 'warn', 'party-leave');
    if (st.reason) note(act, st.reason, '');
    if (c.error) note(act, partyErrorText(c.error), 'err');
  }

  function renderCount(room: PartyRoomView): void {
    const s = sec('');
    const n = countdownSec(room.startAt, now());
    const big = el('div', '', s, n > 0 ? String(n) : '출발!'); big.className = 'count'; big.dataset.testid = 'party-count';
    if (n === 0) big.style.fontSize = '56px';
    note(s, '곧 던전으로 들어가요. 준비하세요!');
  }

  function renderResult(end: NonNullable<PartyClientView['lastEnd']>): void {
    const s1 = sec('');
    const t = el('div', '', s1, endTitle(end)); t.className = 'title ' + (end.ok ? 'win' : 'lose'); t.dataset.testid = 'party-result-title';
    const sub = Math.floor(end.ms / 1000);
    note(s1, '처치 ' + end.kills + ' · 걸린 시간 ' + Math.floor(sub / 60) + '분 ' + (sub % 60) + '초');
    const why = endNote(end);
    if (why) note(s1, why, end.ok ? '' : 'err');
    const s2 = sec('피해 기여');
    const members = c.room?.members ?? [];
    for (const r of damageRows(end.dmg, members, c.you)) {
      const bar = el('div', '', s2); bar.className = 'bar' + (r.me ? ' me' : ''); bar.dataset.testid = 'party-dmg';
      const i = el('i', '', bar); i.style.width = r.pct + '%';
      const b = el('b', '', bar);
      el('span', '', b, r.name + (r.me ? ' (나)' : ''));
      el('span', '', b, r.share + '%');
    }
    if (end.reward) {
      const s3 = sec('보상');
      const rw = el('div', '', s3); rw.className = 'reward';
      const g = el('span', '', rw); g.className = 'pill';
      const im = el('img', '', g); im.alt = ''; im.src = assetUrl('ui/items/cur_gold.webp'); im.onerror = () => { im.style.display = 'none'; };
      el('span', '', g, end.reward.gold.toLocaleString('ko-KR'));
      if (end.reward.items > 0) el('span', '', rw, '장비 ' + end.reward.items + '개').className = 'pill';
      note(s3, rewardWhereText(end.reward));
    }
    const act = sec('', true);
    const row = el('div', '', act); row.className = 'row';
    // 골드만 금고로 가고 장비가 없으면 우편함 단추는 필요 없다
    if (end.reward && (!end.reward.vault || end.reward.items > 0)) btn(row, '우편함에서 받기', () => { dismissedRun = end.runId; if (host.openMail) host.openMail(); else window.dispatchEvent(new CustomEvent<string>(PARTY_MENU_EVENT, { detail: 'mail' })); key = ''; render(); }, 'pri grow', 'party-to-mail');
    btn(row, c.room ? '파티로 돌아가기' : '닫기', () => { dismissedRun = end.runId; key = ''; render(); }, end.reward ? '' : 'pri grow', 'party-result-close');
  }

  function render(): void {
    const room = c.room;
    const v = viewOf(room, c.lastEnd, dismissedRun, village());
    const showWin = opened && village() && v !== 'run';
    win.classList.toggle('on', showWin);
    renderStrip(v, room);
    if (!showWin) { key = ''; return; }
    const sec3 = v === 'count' && room ? countdownSec(room.startAt, now()) : 0;
    const k = [v, c.revision, copied, opened, sec3, c.status, c.error, v === 'result' ? c.lastEnd?.runId : ''].join('|');
    if (k === key) return;
    key = k;
    const keepScroll = bd.scrollTop;
    bd.textContent = ''; ft.textContent = '';
    title.textContent = v === 'result' ? '토벌 결과' : room ? '파티' : '파티';
    if (v === 'result' && c.lastEnd) renderResult(c.lastEnd);
    else if (v === 'count' && room) renderCount(room);
    else if (v === 'lobby' && room) renderLobby(room);
    else renderNone();
    bd.scrollTop = keepScroll;
    const nm = host.heroName?.();
    win.setAttribute('aria-label', '파티' + (nm ? ' · ' + nm : ''));
  }

  function renderStrip(v: PartyView, room: PartyRoomView | null): void {
    const on = v === 'run' && !!room;
    strip.classList.toggle('on', on);
    if (!on || !room) { stripKey = ''; return; }
    const armed = leaveArmedAt > 0 && now() - leaveArmedAt < STRIP_LEAVE_CONFIRM_MS;
    const k = room.wave + '/' + room.waves + '|' + room.members.map((m) => m.slot + m.name + m.online + m.down).join(',') + '|' + c.you + '|' + armed;
    if (k === stripKey) return;
    stripKey = k;
    strip.textContent = '';
    el('div', '', strip, '웨이브 ' + Math.max(1, room.wave) + '/' + room.waves).className = 'w';
    for (const m of room.members) {
      if (m.slot === c.you) continue;
      const s = stripMemberState(m);
      const row = el('div', '', strip); row.className = 'm' + (s === 'ok' ? '' : ' ' + s);
      el('i', '', row);
      el('span', '', row, m.name);
      if (s !== 'ok') el('b', '', row, s === 'down' ? '쓰러짐' : '끊김');
    }
    // 판 도중 포기(PR #243 Codex P2): 일시정지·귀환서가 막혀 있으니 여기서 나간다. 잘못 누르지 않게 두 번 눌러야 나간다(보상 없음)
    btn(strip, armed ? '정말 나가기' : '나가기', () => {
      const r = stripLeaveTap(leaveArmedAt, now());
      leaveArmedAt = r.armedAt;
      if (r.leave) c.leave();
      stripKey = ''; render();
    }, 'lv' + (armed ? ' armed' : ''), 'party-strip-leave');
  }

  // 상태 변화에 따라 창을 자동으로 연다: 카운트 시작, 결과 도착(마을), 새 오류(강퇴 등)
  function autoOpen(): void {
    const room = c.room;
    const v = viewOf(room, c.lastEnd, dismissedRun, village());
    if (v === 'count' && lastView !== 'count' && village()) { opened = true; window.dispatchEvent(new CustomEvent<string>(PARTY_WINDOW_EVENT, { detail: 'party' })); }
    if (v === 'result' && c.lastEnd && c.lastEnd.runId !== seenResult) { seenResult = c.lastEnd.runId; opened = true; window.dispatchEvent(new CustomEvent<string>(PARTY_WINDOW_EVENT, { detail: 'party' })); }
    if (c.error && c.error !== seenErr && (c.error === 'kicked' || c.error === 'replaced' || c.error === 'version') && village()) opened = true;
    seenErr = c.error;
    lastView = v;
  }

  const onMenu = (e: Event): void => {
    if ((e as CustomEvent<string>).detail !== 'party') return;
    if (!village()) return;
    setOpen(!opened);
  };
  const onWin = (e: Event): void => { if ((e as CustomEvent<string>).detail !== 'party' && opened) { opened = false; key = ''; render(); } };
  window.addEventListener(PARTY_MENU_EVENT, onMenu);
  window.addEventListener(PARTY_WINDOW_EVENT, onWin);
  xb.addEventListener('click', () => setOpen(false));
  win.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); e.stopPropagation(); } });

  const tick = (): void => { autoOpen(); if (!village() && opened) opened = false; render(); };
  const timer = setInterval(tick, 200);
  tick();

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!opened),
    isOpen: () => opened && win.classList.contains('on'),
    tick,
    dispose: () => { clearInterval(timer); window.removeEventListener(PARTY_MENU_EVENT, onMenu); window.removeEventListener(PARTY_WINDOW_EVENT, onWin); win.remove(); strip.remove(); },
  };
}
